import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  EmptyState,
  ListPage,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { ArrowRight, CheckCircle2, type LucideIcon, Mail, Smartphone, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ChannelMetrics } from '../api/metrics.api';
import { metricsApi } from '../api/metrics.api';
import { formatPercent, formatRate } from '../utils/format-rate';

type Period = 'today' | 'week' | 'month';

const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

function channelRate(ch: ChannelMetrics): string {
  if (ch.sent === 0) return '--';
  return formatRate(ch.delivered / ch.sent);
}

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  tone: 'info' | 'success' | 'danger' | 'accent';
  loading?: boolean;
}

const TONE_BG: Record<StatCardProps['tone'], string> = {
  info: 'bg-info/10 text-info',
  success: 'bg-success/10 text-success',
  danger: 'bg-destructive/10 text-destructive',
  accent: 'bg-accent/10 text-accent',
};

function StatCard({ icon: Icon, label, value, tone, loading }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div
          className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full ${TONE_BG[tone]}`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-sm text-muted-foreground">{label}</div>
        {loading ? (
          <Skeleton className="mt-1 h-7 w-24" />
        ) : (
          <div className="text-2xl font-bold tracking-tight text-foreground">{value}</div>
        )}
      </CardContent>
    </Card>
  );
}

const QUICK_LINKS = [
  {
    title: 'Manage Notifications',
    desc: 'View and track notification delivery',
    path: '/notifications',
  },
  { title: 'Manage Templates', desc: 'Create and edit email templates', path: '/templates' },
  { title: 'Manage Apps', desc: 'Configure apps and API keys', path: '/apps' },
];

export function DashboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('today');
  const { data, loading, error, refetch } = metricsApi.withQuery({ period }).useRecord();

  const channels = data?.byChannel ?? [];

  return (
    <ListPage
      title="Dashboard"
      description="Operational overview of notification delivery."
      actions={
        <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <TabsList>
            {PERIOD_OPTIONS.map((opt) => (
              <TabsTrigger key={opt.value} value={opt.value}>
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      }
    >
      {error ? (
        <EmptyState
          title="Couldn't load dashboard"
          description={(error as { message?: string }).message ?? 'Try again in a moment.'}
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-primary hover:underline"
            >
              Retry
            </button>
          }
          className="border-destructive/40 bg-destructive/5"
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={Mail}
              label="Total Sent"
              value={data?.totalSent ?? '--'}
              tone="info"
              loading={loading}
            />
            <StatCard
              icon={CheckCircle2}
              label="Delivery Rate"
              value={data ? formatPercent(data.deliveryRate) : '--'}
              tone="success"
              loading={loading}
            />
            <StatCard
              icon={XCircle}
              label="Failed"
              value={data?.failed ?? '--'}
              tone="danger"
              loading={loading}
            />
            <StatCard
              icon={Smartphone}
              label="Active Apps"
              value={data?.activeApps ?? '--'}
              tone="accent"
              loading={loading}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Channel breakdown</CardTitle>
              <CardDescription>
                Delivery performance grouped by channel for the selected period.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-y border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {['Channel', 'Sent', 'Delivered', 'Failed', 'Delivery rate'].map((h, idx) => (
                      <th
                        key={h}
                        scope="col"
                        className={`px-4 py-3 ${idx === 0 ? 'text-left' : 'text-right'}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={`skeleton-${i}`} className="border-b border-border last:border-0">
                        {Array.from({ length: 5 }).map((__, j) => (
                          <td key={`s-${i}-${j}`} className="px-4 py-3">
                            <Skeleton className="h-4 w-16" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : channels.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                      >
                        No channel data available.
                      </td>
                    </tr>
                  ) : (
                    channels.map((metrics) => {
                      const rate = metrics.sent === 0 ? 0 : metrics.delivered / metrics.sent;
                      const tone = rate >= 0.95 ? 'success' : rate >= 0.8 ? 'warning' : 'danger';
                      return (
                        <tr key={metrics.channel} className="border-b border-border last:border-0">
                          <td className="px-4 py-3">
                            <ChannelBadge channel={metrics.channel as ChannelKind} />
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-foreground">
                            {metrics.sent}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-foreground">
                            {metrics.delivered}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-foreground">
                            {metrics.failed}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <StatusBadge tone={tone}>{channelRate(metrics)}</StatusBadge>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            {QUICK_LINKS.map((link) => (
              <button
                type="button"
                key={link.path}
                onClick={() => navigate(link.path)}
                className="group rounded-lg border border-border bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{link.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{link.desc}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </ListPage>
  );
}
